import { api } from '@connected/api-client';
import { navigateWithSSO } from '@connected/api-client/lib/sso';
import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import type { ElectricalProjectInspectionKind } from '@connected/elektra-sdk/validators/electrical-project';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { useValidation } from '@/components/features/validation-errors';
import { useOffline } from '@/stores/network-status';

export function useCreateReport({ electricalProjectVersionId }: { electricalProjectVersionId: string }) {
    const [validationModalOpen, setValidationModalOpen] = useState(false);

    const isUserOffline = useOffline();
    const isMutating = useIsMutating();

    const { context } = useElectricalProject();
    const isCombinationInspection = context.isCombinationInspection ?? false;

    const [enabledKinds, setEnabledKinds] = useState<Set<ElectricalProjectInspectionKind>>(() => {
        return new Set(context.selectedTemplates.map(t => t.inspectionKind));
    });

    function toggleKind(kind: ElectricalProjectInspectionKind) {
        setEnabledKinds((prev) => {
            const next = new Set(prev);
            if (next.has(kind)) {
                next.delete(kind);
            }
            else {
                next.add(kind);
            }
            return next;
        });
    }

    const { data: projectData } = useQuery(elektraAppQueryOptions.project.edit({ electricalProjectVersionId }));

    const createDocumentMutation = api.useMutation(
        'post',
        '/api/v2/electrical/projects/documents/create',
        {
            onSuccess: () => {
                navigateWithSSO({
                    targetDomain: 'portaal',
                    redirectPath: `/dashboard/organizations/${projectData?.Project?.OrganizationId}/projects/${electricalProjectVersionId}/documents`,
                });
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    );

    const { validationItems, refetch: refetchValidation } = useValidation({
        electricalProjectVersionId,
        kind: 'Reports',
    });

    async function handleDocumentCreation() {
        if (isMutating) {
            toast.error('Er zijn nog acties bezig. Probeer het later opnieuw.');
            return;
        }

        const validation = await refetchValidation();

        if (validation.data?.ValidationResults?.length !== 0) {
            setValidationModalOpen(true);
            toast.error('Er zijn nog fouten in de inspectie, deze moeten eerst worden opgelost.');
            return;
        }

        createDocumentMutation.mutate({
            body: {
                ElectricalProjectVersionId: electricalProjectVersionId,
                Kind: 'Reports',
            },
        });
    }

    function handleOpenChange(open: boolean) {
        if (open) {
            refetchValidation();
        }
        else {
            if (createDocumentMutation.isPending) {
                toast.error('Document aanmaken is nog bezig, probeer het later opnieuw.');
            }
            else {
                setValidationModalOpen(false);
            }
        }
    }

    function forceCreate() {
        createDocumentMutation.mutate({
            body: {
                ElectricalProjectVersionId: electricalProjectVersionId,
                Kind: 'Reports',
            },
        });
    }

    const kindLabels: Record<string, string> = {
        Scope8: 'Scope 8',
        Scope10: 'Scope 10',
        Scope12: 'Scope 12',
        Nen1010: 'NEN 1010',
        Nen3140: 'NEN 3140',
    };

    const buttonLabel = isCombinationInspection
        ? `${Array.from(enabledKinds).map(k => kindLabels[k] ?? k).join(' + ')} rapport genereren`
        : 'Inspectie afronden en rapport maken';

    return {
        handleDocumentCreation,
        isCreating: createDocumentMutation.isPending,
        isOffline: isUserOffline,
        isMutating: isMutating > 0,
        validationModalOpen,
        setValidationModalOpen,
        handleOpenChange,
        validationItems,
        buttonLabel,
        isCombinationInspection,
        enabledKinds,
        toggleKind,
        forceCreate,
    };
}
